import React, { useContext, useState, useEffect } from 'react'
import { Grid, Cell, Expander, ExpanderHeader, ExpanderContent, ToggleGroup, Toggle } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import DataView from '../../common/DataView'
import { UserLink } from '../../app/Links'
import { OwsContext } from '../OwsContext'
import { utcToLocaleString } from '../../../util/DateUtil'
import { usePageTitle } from '../../app/PageTitleContext'

export default function OwsChanges() {
  const { t } = useTranslation()
  usePageView({ page_detail: 'DMConsole:OWSChanges' })
  usePageTitle(t('pageTitle.ows.changes'))
  const owsContext = useContext(OwsContext)
  const owsChangesList = owsContext?.owsChangesList
  const [toggle, setToggle] = useState(true)

  return (
    <>
      {owsChangesList && owsChangesList.length > 0 && (
        <ToggleGroup>
          <Toggle
            label="Open All"
            name="allOpen"
            checked={toggle}
            onChange={({ target }) => setToggle(target.checked)}
          />
        </ToggleGroup>
      )}
      <Grid id="OWS Changes">
        {owsChangesList &&
          owsChangesList.length > 0 &&
          owsChangesList.map((owsChange) => {
            const keyString = Object.keys(owsChange)
              .filter((key) => {
                return typeof owsChange[key] === 'string'
              })
              .map((key) => {
                return owsChange[key]
              })
            return <OwsChange owsChange={owsChange} toggle={toggle} key={keyString.join('')} />
          })}
      </Grid>
    </>
  )
}

function OwsChange({ owsChange, toggle }) {
  const [open, setOpen] = useState()

  useEffect(() => {
    setOpen(toggle)
  }, [toggle, setOpen])

  return (
    <Cell columns={12}>
      <div style={{ border: '1px solid #efefef', borderRadius: '5px', padding: '9px' }}>
        <Expander open={open} setOpen={setOpen}>
          <ExpanderHeader>
            <OwsChangeSetHeader owsChange={owsChange} />
          </ExpanderHeader>
          <ExpanderContent>
            <div style={{ marginTop: '10px' }}>
              <Grid>
                <Cell columns={12}>
                  <OwsChangeSetInfo owsChange={owsChange} />
                </Cell>
                <Cell columns={12}>
                  <OwsChangeTable owsChange={owsChange} />
                </Cell>
              </Grid>
            </div>
          </ExpanderContent>
        </Expander>
      </div>
    </Cell>
  )
}

function OwsChangeSetHeader({ owsChange }) {
  const { t } = useTranslation()
  return (
    <Grid guttersY="none">
      <Cell columns={3}>
        <Grid guttersY="none">
          <Cell columns={1}>
            <strong>{t('changes.dateChanged', 'Change Date')}: </strong>
          </Cell>
          <Cell columns={2}>{utcToLocaleString(owsChange.createDate, true)}</Cell>
        </Grid>
      </Cell>
      <Cell columns={3}>
        <Grid guttersY="none">
          <Cell columns={1}>
            <strong>{t('changes.modifiedBy', 'Modified By')}: </strong>
          </Cell>
          <Cell columns={2}>
            {owsChange.changeSource?.treeContributorId ? (
              <UserLink userId={owsChange.changeSource.treeContributorId} />
            ) : (
              <>{t('changes.na', 'N/A')}</>
            )}
          </Cell>
        </Grid>
      </Cell>
      <Cell columns={6}>
        <Grid guttersY="none">
          <Cell columns={1}>
            <strong>{t('changes.changeReason', 'Change Reason')}: </strong>
          </Cell>
          <Cell columns={5}>
            {owsChange.reasonDescription === '' || owsChange.reasonDescription === null
              ? '(No reason given)'
              : owsChange.reasonDescription}
          </Cell>
        </Grid>
      </Cell>
    </Grid>
  )
}

function OwsChangeSetInfo({ owsChange }) {
  const { t } = useTranslation()
  return (
    <Grid>
      <Cell columns={2}>
        <strong>{t('changes.systemType', 'System Type')}: </strong>
      </Cell>
      <Cell columns={2}>{owsChange.changeSource.systemType}</Cell>
      <Cell columns={2}>
        <strong>{t('reasonType', 'Reason Type')}: </strong>
      </Cell>
      <Cell columns={3}>{owsChange.changeReason.replace(/_/g, ' ')}</Cell>
    </Grid>
  )
}

function OwsChangeTable({ owsChange }) {
  return (
    <Grid>
      {owsChange.changes.map((change) => {
        const keyString = Object.keys(change).filter((key) => change[key])

        const keyValues = Object.keys(change)
          .filter((key) => change[key])
          .map((key) => {
            let detail = change[key]
            if (typeof change[key] === 'string' && change[key].includes('http')) {
              detail = detail.split('/')
              detail = detail[detail.length - 1]
            }
            return { label: key, detail }
          })
        if (change.changedResource && Object.keys(change.changedResource).length > 0) {
          Object.keys(change.changedResource)
            .filter((key) => key !== '')
            .forEach((key) => keyValues.push({ label: key, detail: change.changedResource[key] }))
        }
        return (
          <Cell columns={12} key={keyString.join('')}>
            <DataView data={keyValues} />
          </Cell>
        )
      })}
    </Grid>
  )
}
